import { ApiService } from "services/server";
import { NewsList } from "views";
import { CONTENT_TYPES, TAG_TREES } from "utils/constants";
import { Formatter } from "utils/formatter";
import {
  normalizeConferenceTagTrees,
  normalizeMetaTags,
} from "utils/normalizer";
import type { GetServerSideProps } from "next";
import type { GetContentParams } from "types";

const TABS = ["speechs", "gathering"] as const;
const MAPED_TABS = {
  fa: {
    [TABS[0]]: "کنفرانس و سخنرانی‌ها",
    [TABS[1]]: "گردهمایی و همایش‌ها",
  },
  en: {
    [TABS[0]]: "Conferences and Speeches",
    [TABS[1]]: "Gatherings and Meetings",
  },
} as const;

const MAPED_TAG_TREES = {
  fa: {
    [TABS[0]]: TAG_TREES.FA.CONFERENCES_SPEECH,
    [TABS[1]]: TAG_TREES.FA.CONFERENCES_GATHERING,
  },
  en: {
    [TABS[0]]: TAG_TREES.EN.CONFERENCES_SPEECH,
    [TABS[1]]: TAG_TREES.EN.CONFERENCES_GATHERING,
  },
} as const;

const ITEMS_PER_PAGE = 6;

const BREADCRUMB = {
  fa: [{ title: "کنفرانس و گردهمایی‌ها" }],
  en: [{ title: "Conferences and Gatherings" }],
};

function NewsListPage({ data }) {
  return (
    <NewsList
      data={data}
      mappedTabs={MAPED_TABS}
      tabs={TABS}
      mappedTagTrees={MAPED_TAG_TREES}
      breadcrumb={BREADCRUMB}
      contentType={CONTENT_TYPES.CONFERENCE}
    />
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  query,
  locale,
}) => {
  let noIndex = false;
  const apiService = new ApiService(locale);

  // Check if query string is present
  if (Object.keys(query).length > 0) {
    noIndex = true;
  }

  try {
    const page = Number.isNaN(Number(query.page)) ? 1 : Number(query.page);

    const tagTrees = [
      query.category
        ? query.category
        : MAPED_TAG_TREES[locale]?.[query?.tab || TABS[0]],
    ];

    const params: GetContentParams = {
      tagTrees,
      offset: (page - 1) * ITEMS_PER_PAGE,
      size: ITEMS_PER_PAGE,
      orderBy: ["content.publish_date:desc"],
      orderTagTree: "child",
    };
    if (query.search) {
      params.fieldCode = ["title"];
      params.fieldValue = [query.search];
    }
    const requests = [
      apiService.getConferenceList(params, { formatter: false }), // 0
      apiService.getGatheringTagTrees(), // 1
      apiService.getSpeechTagTrees(), // 2
      apiService.getSpeechMetaTags(), // 3
      apiService.getConferenceMetaTags(), // 4
    ];

    let response = [];
    try {
      response = await ApiService.multipleRequest(requests);
    } catch (e) {
      console.log(`e`, e);
    }
    const newsTagTrees = normalizeConferenceTagTrees(response[1]);
    const eventsTagTrees = normalizeConferenceTagTrees(response[2]);
    const cmsCount = response[0]?.count || 0;
    const totalPages = Math.ceil(cmsCount / ITEMS_PER_PAGE);

    if (page > totalPages) {
      noIndex = true;
    }

    return {
      props: {
        data: {
          cmsData: Formatter.formatData({ data: response[0] }),
          cmsCount: response[0]?.count || 0,
          totalPages,
          page,
          newsTagTrees,
          eventsTagTrees,
          newsMetaTag: normalizeMetaTags(response[3]),
          eventsMetaTag: normalizeMetaTags(response[4]),
          defaultTitle:
            locale === "fa"
              ? "کنفرانس و گردهمایی‌ها"
              : "Conferences & Meetings",
          noIndex,
          defaultValues: {
            search: query.search || "",
            category: query.category
              ? [newsTagTrees, eventsTagTrees][
                  query.tab === TABS[1] ? 1 : 0
                ].find((item) => item?.value === query.category) || ""
              : "",
          },
        },
      },
    };
  } catch (err) {
    console.log("err", err);
    return {
      redirect: {
        destination: "/500",
        permanent: false,
      },
    };
  }
};

export default NewsListPage;
